import { useState, useEffect } from "react";
import axios from "axios";
import {
  Bell, Plus, Edit2, Trash2, Send, X,
  Users, BarChart2, CheckCircle, Layers,
  ChevronDown, AlertCircle, Link, Image, RefreshCw,
} from "lucide-react";
import { useAdmin } from "../admin-context";
import DataTable from "../DataTable";
import { apiUrl } from "../../../utils/config";

// ── Constants ─────────────────────────────────────────────────────────────────

const RECIPIENT_GROUPS = [
  { value: "all_customers",   label: "All Subscribers"  },
  { value: "registered",      label: "Registered Users" },
];

const TEMPLATES = [
  { id: "promo",   label: "Promo",    title: "Special offer just for you 🎁", body: "Use code SAVE15 for 15% off your next order. Limited time only!" },
  { id: "flash",   label: "Flash",    title: "Flash sale is live! ⚡",         body: "Huge discounts for the next 3 hours. Order now before it's gone." },
  { id: "loyalty", label: "Loyalty",  title: "Your points are waiting 🏆",     body: "You have loyalty points you haven't used. Redeem them on your next order!" },
  { id: "order",   label: "Order",    title: "Your order is on its way 🛵",    body: "Your order has been picked up and is heading to you. Track it live." },
];

const STATUS_CHIP = {
  draft:     "bg-gray-100 text-gray-600",
  scheduled: "bg-blue-100 text-blue-700",
  sent:      "bg-green-100 text-green-700",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const ctr = (sent, clicked) =>
  sent > 0 ? `${((clicked / sent) * 100).toFixed(1)}%` : "—";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const groupLabel = (val) =>
  RECIPIENT_GROUPS.find(g => g.value === val)?.label || val;

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 ${color}`}>
      <Icon size={20} />
    </div>
    <div>
      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const PushPreview = ({ title, body, iconUrl }) => (
  <div className="bg-gray-100 rounded-2xl p-4">
    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-3">Browser Preview</p>
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-3 flex items-start gap-3 max-w-sm">
      <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {iconUrl ? (
          <img src={iconUrl} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <Bell size={18} className="text-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900 truncate">
          {title || <span className="text-gray-300 italic font-normal">Notification title…</span>}
        </p>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
          {body || <span className="text-gray-300 italic">Message body appears here…</span>}
        </p>
        <p className="text-[10px] text-gray-400 mt-1.5">gamapa.com · now</p>
      </div>
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const PushNotificationsPage = () => {
  const { showToast, logAudit } = useAdmin();

  const [campaigns, setCampaigns]     = useState([]);
  const [subscriberCount, setSubCount] = useState(0);
  const [loadingList, setLoadingList]  = useState(true);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editing, setEditing]         = useState(null);
  const [saving, setSaving]           = useState(false);
  const [sending, setSending]         = useState(null); // campaign id being sent

  const blank = () => ({
    title: "", body: "", url: "", icon_url: "",
    recipient_group: "all_customers", scheduled_at: "",
  });
  const [form, setForm] = useState(blank());
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Load data ────────────────────────────────────────────────────────────

  const loadCampaigns = async () => {
    setLoadingList(true);
    try {
      const [campRes, subRes] = await Promise.all([
        axios.get(`${apiUrl}/push/campaigns`),
        axios.get(`${apiUrl}/push/subscriber-count`),
      ]);
      if (campRes.data.status) setCampaigns(campRes.data.campaigns || []);
      if (subRes.data.status)  setSubCount(subRes.data.count || 0);
    } catch {
      showToast("Failed to load campaigns.", "error");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadCampaigns(); }, []); // eslint-disable-line

  // ── Drawer helpers ────────────────────────────────────────────────────────

  const openAdd = () => { setEditing(null); setForm(blank()); setDrawerOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      title: c.title, body: c.body, url: c.url || "",
      icon_url: c.icon_url || "", recipient_group: c.recipient_group,
      scheduled_at: c.scheduled_at ? c.scheduled_at.slice(0, 16) : "",
    });
    setDrawerOpen(true);
  };

  const applyTemplate = (id) => {
    const t = TEMPLATES.find(t => t.id === id);
    if (t) setForm(f => ({ ...f, title: t.title, body: t.body }));
  };

  // ── Save draft ────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      showToast("Title and message body are required.", "error");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${apiUrl}/push/campaigns/${editing.id}`, form);
        logAudit("Updated", "Push Campaign", form.title);
        showToast("Campaign updated.", "success");
      } else {
        await axios.post(`${apiUrl}/push/campaigns`, form);
        logAudit("Created", "Push Campaign", form.title);
        showToast("Saved as draft.", "success");
      }
      setDrawerOpen(false);
      loadCampaigns();
    } catch {
      showToast("Failed to save campaign.", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Send campaign ─────────────────────────────────────────────────────────

  const handleSendNow = async (campaignId, title) => {
    setSending(campaignId);
    try {
      const { data } = await axios.post(`${apiUrl}/push/campaigns/${campaignId}/send`);
      if (data.status) {
        showToast(`Sent to ${data.sent} subscribers!`, "success");
        logAudit("Sent", "Push Campaign", title);
        loadCampaigns();
      } else {
        showToast(data.error || "Send failed.", "error");
      }
    } catch {
      showToast("Failed to send campaign.", "error");
    } finally {
      setSending(null);
    }
  };

  // ── Save + send from drawer ───────────────────────────────────────────────

  const handleSaveAndSend = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      showToast("Title and message body are required.", "error");
      return;
    }
    setSaving(true);
    try {
      let id = editing?.id;
      if (editing) {
        await axios.put(`${apiUrl}/push/campaigns/${editing.id}`, form);
      } else {
        const { data } = await axios.post(`${apiUrl}/push/campaigns`, form);
        id = data.campaign?.id;
      }
      setDrawerOpen(false);
      await handleSendNow(id, form.title);
    } catch {
      showToast("Failed.", "error");
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    const c = campaigns.find(x => x.id === id);
    try {
      await axios.delete(`${apiUrl}/push/campaigns/${id}`);
      logAudit("Deleted", "Push Campaign", c?.title || id);
      showToast("Campaign deleted.", "success");
      loadCampaigns();
    } catch {
      showToast("Failed to delete.", "error");
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────

  const totalSent      = campaigns.filter(c => c.status === "sent").length;
  const totalScheduled = campaigns.filter(c => c.status === "scheduled").length;
  const totalClicks    = campaigns.reduce((s, c) => s + Number(c.clicked_count || 0), 0);

  // ── Table columns ─────────────────────────────────────────────────────────

  const columns = [
    {
      key: "title", label: "Notification", sortable: true,
      render: (val, row) => (
        <div>
          <p className="font-semibold text-gray-800 text-sm">{val}</p>
          <p className="text-xs text-gray-400 truncate max-w-[220px]">{row.body}</p>
        </div>
      ),
    },
    {
      key: "recipient_group", label: "Recipients", sortable: true,
      render: (val) => (
        <span className="flex items-center gap-1.5 text-sm text-gray-600">
          <Users size={13} className="text-gray-400" /> {groupLabel(val)}
        </span>
      ),
    },
    {
      key: "status", label: "Status", sortable: true,
      render: (val) => (
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${STATUS_CHIP[val] || "bg-gray-100 text-gray-600"}`}>
          {val}
        </span>
      ),
    },
    {
      key: "sent_count", label: "Sent", sortable: true,
      render: (val) => <span className="text-sm font-medium text-gray-700">{Number(val || 0).toLocaleString()}</span>,
    },
    {
      key: "clicked_count", label: "CTR", sortable: false,
      render: (val, row) => (
        <span className="text-sm font-medium text-gray-700">{ctr(row.sent_count, val)}</span>
      ),
    },
    {
      key: "created_at", label: "Date", sortable: true,
      render: (val) => <span className="text-sm text-gray-400">{fmtDate(val)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Bell size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Push Notifications</h1>
            <p className="text-xs text-gray-400">Send browser push campaigns to your subscribers</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadCampaigns}
            className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={15} className={loadingList ? "animate-spin" : ""} />
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm transition"
          >
            <Plus size={16} /> New Campaign
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users}       label="Subscribers" value={subscriberCount}               color="bg-purple-500" />
        <StatCard icon={CheckCircle} label="Sent"         value={totalSent}                     color="bg-green-500"  />
        <StatCard icon={BarChart2}   label="Clicks"       value={totalClicks.toLocaleString()}  color="bg-orange-500" />
        <StatCard icon={Bell}        label="Scheduled"    value={totalScheduled}                color="bg-blue-500"   />
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={campaigns}
        searchKeys={["title", "body"]}
        searchPlaceholder="Search campaigns…"
        filters={[
          {
            key: "status",
            label: "Status",
            options: [
              { label: "Draft",     value: "draft"     },
              { label: "Scheduled", value: "scheduled" },
              { label: "Sent",      value: "sent"      },
            ],
          },
        ]}
        defaultPageSize={10}
        emptyText="No push campaigns yet. Create your first one!"
        actions={(row) => (
          <div className="flex items-center gap-1.5">
            {row.status !== "sent" && (
              <button
                onClick={() => handleSendNow(row.id, row.title)}
                disabled={sending === row.id}
                className="p-2 rounded-lg border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 transition disabled:opacity-40"
                title="Send now"
              >
                {sending === row.id ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            )}
            <button
              onClick={() => openEdit(row)}
              className="p-2 rounded-lg border border-orange-200 bg-orange-50 text-orange-600 hover:bg-orange-100 transition"
              title="Edit"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              className="p-2 rounded-lg border border-red-100 bg-red-50 text-red-500 hover:bg-red-100 transition"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      />

      {/* Create / Edit Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editing ? "Edit Push Campaign" : "New Push Campaign"}
      >
        <div className="space-y-5">

          {/* Templates */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Load Template</label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t.id)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 transition"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Title * <span className="text-gray-300 font-normal">({form.title.length}/50)</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={e => set("title", e.target.value.slice(0, 50))}
              placeholder="e.g. Flash Sale is Live! 🔥"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Message Body * <span className="text-gray-300 font-normal">({form.body.length}/120)</span>
            </label>
            <textarea
              value={form.body}
              onChange={e => set("body", e.target.value.slice(0, 120))}
              rows={3}
              placeholder="Short message shown under the title…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition resize-none"
            />
          </div>

          {/* URL */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
              <Link size={11} /> Click URL
            </label>
            <input
              type="text"
              value={form.url}
              onChange={e => set("url", e.target.value)}
              placeholder="e.g. /menu or https://gamapa.com/flash"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>

          {/* Icon URL */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1">
              <Image size={11} /> Icon URL <span className="text-gray-300 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={form.icon_url}
              onChange={e => set("icon_url", e.target.value)}
              placeholder="https://…/icon.png"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>

          {/* Recipient group */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Recipients</label>
            <div className="relative">
              <select
                value={form.recipient_group}
                onChange={e => set("recipient_group", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 appearance-none bg-white transition pr-8"
              >
                {RECIPIENT_GROUPS.map(g => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Schedule <span className="text-gray-300 font-normal">(leave blank to save as draft)</span>
            </label>
            <input
              type="datetime-local"
              value={form.scheduled_at}
              onChange={e => set("scheduled_at", e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>

          {/* Preview */}
          <PushPreview title={form.title} body={form.body} iconUrl={form.icon_url} />

          {/* Warning */}
          {(!form.title.trim() || !form.body.trim()) && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded-xl px-3 py-2">
              <AlertCircle size={13} /> Title and message body are required before sending.
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition"
            >
              {saving ? "Saving…" : "Save Draft"}
            </button>
            <button
              type="button"
              onClick={handleSaveAndSend}
              disabled={saving || !form.title.trim() || !form.body.trim()}
              className="flex-1 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition shadow-sm"
            >
              <Send size={15} /> {saving ? "Sending…" : "Send Now"}
            </button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default PushNotificationsPage;
